import { Button } from "@components/ui/button"
import { useAccountOfferFunnel } from "../utils/useAccountOfferFunnel"
import { useSerializeOfferScript } from "../utils/useSerializeOfferScript"
import { Loader2, UserPen } from "lucide-react"
import { useDrawer } from "@contexts/DrawerContext"
import { Input } from "@components/ui/input"
import { useForm } from "react-hook-form"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@components/ui/form"
import {
  useAcceptWinbackOfferMutation,
  useGetWinbackQuery,
} from "@apis/winbacks/useWinbacks"
import type { IWinbackOffer } from "@apis/winbacks/types"
import { toast } from "sonner"
import { useAcceptRetentionOfferMutation } from "@apis/retention/useRetention"
import type { IRetentionOffer } from "@apis/retention/types"
import { accountKeys } from "@apis/accounts/queryKeys"
import { useQueryClient } from "@tanstack/react-query"
import type { TVerificationForm } from "@utils/verificationScriptsUtils"
import { VerificationChecklist } from "@components/VerificationChecklist"
import { AccountContactPreferencesForm } from "@components/AccountNew/AccountContactPreferencesForm/AccountContactPreferencesForm"

export const Verification = () => {
  const {
    offerType,
    setStep,
    account_id,
    selectedOffer,
    tpvType,
    setConfirmedOrder,
  } = useAccountOfferFunnel()
  const { openDrawer } = useDrawer()

  const { script, market, account_number, prospect_guid } =
    useSerializeOfferScript()

  const queryClient = useQueryClient()
  const getWinbackQuery = useGetWinbackQuery(
    account_id,
    offerType === "Winback",
  )

  const acceptWinbackOfferMutation = useAcceptWinbackOfferMutation()
  const acceptRetentionOfferMutation = useAcceptRetentionOfferMutation()

  const form = useForm<TVerificationForm>({
    defaultValues: {
      account_number: account_number || "",
      verificationItem: script.reduce(
        (acc, _, index) => {
          acc[index.toString()] = false
          return acc
        },
        {} as Record<string, boolean>,
      ),
    },
    mode: "onChange",
  })

  const onSubmit = (values: TVerificationForm) => {
    return offerType === "Winback"
      ? handleSubmitWinback(values)
      : handleSubmitRetention()
  }

  const handleSubmitWinback = (values: TVerificationForm) => {
    const selectedWinbackOffer = selectedOffer as IWinbackOffer

    acceptWinbackOfferMutation.mutate(
      {
        account_id,
        offer_code: selectedWinbackOffer?.offer_code as string,
        request: {
          winback_id: getWinbackQuery.data?.winback_id as number,
          lock_from_dt: selectedWinbackOffer?.lock_from_dt as string,
          lock_to_dt: selectedWinbackOffer?.lock_to_dt as string,
          account_winback_status_history_id: selectedWinbackOffer
            ?.account_winback_status_history
            .account_winback_status_history_id as number,
          offered_dt: selectedWinbackOffer?.account_winback_status_history
            .offered_dt as string,
          campaign_id: selectedWinbackOffer?.campaign_id as string,
          tpv_type: tpvType as string,
          conversion_medium: "INSPIRE_CRM",
          account_number: values.account_number as string,
        },
      },
      {
        onSuccess: (res) => {
          setConfirmedOrder(res)
          toast.success("Successfully updated submitted Winback order!")
          setStep("confirmation")
          queryClient.invalidateQueries({
            queryKey: accountKeys.detail(account_id),
          })
        },
        onError: (error) => {
          toast.error(
            `There was a problem saving the winback offer. Please try again. ${error}`,
            { duration: 10000 },
          )
          setStep("initial")
        },
      },
    )
  }

  const handleSubmitRetention = () => {
    const selectedRetentionOffer = selectedOffer as IRetentionOffer

    acceptRetentionOfferMutation.mutate(
      {
        account_id,
        offer_code: selectedRetentionOffer?.offer_code as string,
        request: {
          lock_from_dt: selectedRetentionOffer?.retention_details
            ?.lock_from_dt as string,
          lock_to_dt: selectedRetentionOffer?.retention_details
            ?.lock_to_dt as string,
          offered_dt:
            selectedRetentionOffer?.account_retention_status_history.offered_dt,
          campaign_id: selectedRetentionOffer?.campaign_id as string,
          offer_group_guid: selectedRetentionOffer?.offer_group_guid as string,
          tpv_type: tpvType as string,
          conversion_medium: "INSPIRE_CRM",
        },
      },
      {
        onSuccess: (res) => {
          setConfirmedOrder(res)
          toast.success("Successfully updated submitted Retention order!")
          setStep("confirmation")
        },
        onError: (error) => {
          toast.error(
            `There was a problem saving the retention offer. Please try again. ${error}`,
            { duration: 10000 },
          )
          setStep("initial")
        },
      },
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <h2 className="tw:mb-0 tw:text-xl tw:font-semibold">Are You Sure?</h2>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="tw:flex tw:flex-col tw:gap-2"
        >
          {offerType === "Winback" && (
            <FormField
              control={form.control}
              name="account_number"
              rules={{
                required: `${market?.account_num_synonym} is required.`,
                pattern: {
                  value: new RegExp(
                    market?.validation_rules.account_number_regexp || ".*",
                  ),
                  message: `Please enter a valid ${market?.account_num_synonym}. Example: ${market?.validation_rules.account_number_example}`,
                },
              }}
              render={({ field }) => (
                <FormItem className="tw:flex tw:flex-col tw:space-y-1">
                  <FormLabel htmlFor="account_number">
                    Verify {market?.account_num_synonym}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      id="account_number"
                      placeholder={`Enter ${market?.account_num_synonym}`}
                    />
                  </FormControl>
                  {form.formState.errors.account_number && (
                    <p className="tw:text-sm tw:text-destructive">
                      {form.formState.errors.account_number.message}
                    </p>
                  )}
                </FormItem>
              )}
            />
          )}

          <VerificationChecklist script={script} form={form} />

          <div className="tw:flex tw:justify-between">
            <Button
              variant="warning"
              onClick={() => setStep("offerDetail")}
              type="button"
            >
              Go Back
            </Button>

            <div className="tw:flex tw:gap-3">
              <Button
                type="button"
                onClick={() =>
                  openDrawer(
                    <AccountContactPreferencesForm
                      prospect_guid={prospect_guid}
                    />,
                    "Update Contact Preferences",
                  )
                }
                variant="outline"
              >
                <UserPen />
              </Button>

              <Button
                type="submit"
                disabled={
                  !form.formState.isValid ||
                  acceptWinbackOfferMutation.isPending ||
                  acceptRetentionOfferMutation.isPending
                }
              >
                {acceptWinbackOfferMutation.isPending ||
                acceptRetentionOfferMutation.isPending ? (
                  <>
                    Submitting Order
                    <Loader2 className="tw:animate-spin" />
                  </>
                ) : (
                  "Accept Offer"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
